// Pre-render laz.io redirect, mirroring ssoCallbackBootstrap: imported for
// side effects from src/index.js so it runs the moment the bundle is evaluated,
// before React mounts anything.
//
// Why this exists alongside the Express 301: the 301 only covers documents
// served by our own Node process. Anything served from cache or a CDN in front
// of it never reaches the middleware, and without this the redirect would not
// happen until React boots, PageLoader paints, the lazy BasePage chunk is
// fetched over the network, and the legacy chrome paints - the flash QA
// reported.
//
// SCOPE: initial document loads only. This runs once when src/index.js is
// evaluated and does not rerun on a React Router transition, so in-app
// navigations to /buynow (MarkerPopup.js, Lot.js) keep today's behavior and are
// still redirected by the Routes.js backstop after BasePage paints.
import {
  resolveBuynowRedirect,
  resolveAirportRedirect,
} from "./redirect-config";

// Set on <html> by the inline script in public/index.html, which paints the
// blue holding screen. Kept in sync by a test in redirectBootstrap.test.js.
export const REDIRECT_HOLDING_CLASS = "laz-redirect-holding";

// Path scoping is NOT optional. The sc-based configs declare no `paths`, and
// matchesPath returns true when config.paths is absent, so shouldRedirect is
// truthy for /summary?l=5179&sc=t2p. That is harmless today only because
// Routes.js calls it inside the /buynow <Route>. Gate on the pathname first, or
// this starts redirecting /search-results, /summary and /transactions.
//
// The leading slash is load bearing: it keeps /ebuynow out. Matching a trailing
// segment rather than the whole string keeps REACT_APP_CONTEXT_ROOT working.
const BUYNOW_PATHNAME = /\/buynow\/?$/;

let redirected = false;

function removeHoldingClass() {
  try {
    const root = typeof document !== "undefined" && document.documentElement;
    if (root && root.classList) {
      root.classList.remove(REDIRECT_HOLDING_CLASS);
    }
  } catch (error) {
    // nothing left to fall back to; never let this be the thing that throws
  }
}

/**
 * The /buynow arm: the sales-channel, widget and Canadian handoffs to
 * <new app>/p/<id>.
 *
 * @param {Location} location
 * @returns {string|null} - The absolute target URL, or null to fall through
 */
function resolveBuynowTarget(location) {
  // Path scoping is NOT optional - see BUYNOW_PATHNAME.
  if (!BUYNOW_PATHNAME.test(location.pathname || "")) return null;

  // Host, allowlist, country and target origin all live in
  // resolveBuynowRedirect, so this layer and the Express 301 cannot disagree
  // about which URLs qualify or where they land.
  return resolveBuynowRedirect(
    location.hostname,
    location.pathname,
    new URLSearchParams(location.search || "")
  );
}

/**
 * The airport arm: client roots that moved to the new app, laz.io/a/<client>.
 *
 * Host, allowlist, path shape and query handling all live in
 * resolveAirportRedirect, so this layer and the Express 301 cannot disagree
 * about which URLs qualify.
 *
 * @param {Location} location
 * @returns {string|null} - The absolute target URL, or null to fall through
 */
function resolveAirportTarget(location) {
  return resolveAirportRedirect(
    location.hostname,
    location.pathname,
    new URLSearchParams(location.search || "")
  );
}

/**
 * Redirect to laz.io if the current URL qualifies, under either rule.
 *
 * @param {Location} [location] - injectable for tests
 * @returns {boolean} - whether a navigation was started
 */
export function redirectIfNeeded(location = window.location) {
  if (redirected) return false;

  try {
    // The holding class comes off only when BOTH rules decline. Evaluated in
    // registration order; the two cannot both match, since /buynow is not an
    // allowlisted airport client id.
    const target =
      resolveBuynowTarget(location) || resolveAirportTarget(location);
    if (!target) {
      removeHoldingClass();
      return false;
    }

    // replace(), never `href =`. Routes.js uses href, which is a history PUSH:
    // the back button lands on /buynow, which redirects again. The holding
    // class stays on so the blue screen covers the navigation.
    redirected = true;
    location.replace(target);
    return true;
  } catch (error) {
    // This module is imported for side effects from src/index.js, so a throw
    // here means the entire app fails to boot on every route for every user.
    //
    // Deliberately does NOT call BugsnagManager: this runs before
    // Bugsnag.start() in src/index.js. And the holding class MUST come off, or
    // React boots underneath a permanent blue overlay.
    redirected = false;
    removeHoldingClass();
    return false;
  }
}

/**
 * Whether this page load has already started a navigation to laz.io. Routes.js
 * reads this to avoid a second, superseding navigation.
 *
 * @returns {boolean}
 */
export function hasRedirectedToLazIo() {
  return redirected;
}

/**
 * Backstop for the airport rule, called from the Routes.js airport branch.
 *
 * redirectIfNeeded runs once at module evaluation and never reruns, so a
 * client-side push from /flycae/park back to /flycae would otherwise stay on
 * the legacy app. Goes through the strict host allowlist, deliberately NOT
 * through REDIRECT_CONFIG.getTargetDomain in Routes.js - that older resolver
 * matches hostnames by substring.
 *
 * @param {Location} [location] - injectable for tests
 * @returns {boolean} - whether a navigation was started
 */
export function redirectAirportIfNeeded(location = window.location) {
  if (redirected) return false;

  try {
    const target = resolveAirportTarget(location);
    if (!target) return false;

    redirected = true;
    location.replace(target);
    return true;
  } catch (error) {
    // Routes.js renders straight after this returns; a throw here would blank
    // the airport page entirely.
    redirected = false;
    return false;
  }
}

export function resetRedirectBootstrapForTests() {
  redirected = false;
}

if (typeof window !== "undefined") {
  try {
    redirectIfNeeded();
  } catch (error) {
    // redirectIfNeeded already swallows everything; this is belt and braces
    // around the module-evaluation path itself.
  }
}
